import { useCallback, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import RestartIcon from '@material-ui/icons/Replay';
import SyncIcon from '@material-ui/icons/Sync';
import { InfoCard } from '@backstage/core-components';
import {
  alertApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

// Shape returned by the backend's /api/argocd-sync/.../status
// (packages/backend/src/plugins/argocdSync).
export type ArgocdSyncStatus = {
  instance?: string;
  app: string;
  syncStatus?: string;
  healthStatus?: string;
  operationPhase?: string;
  running: boolean;
  finishedAt?: string;
  canSync: boolean;
  canRestart?: boolean;
  deployments?: { name: string; namespace?: string }[];
  // From the Deployments' live state: a rollout is going (e.g. right after a
  // restart), and when the restart action last ran.
  restarting?: boolean;
  lastRestartAt?: string;
  failed?: string[];
};

// The button is always available, except while a sync (ours or anyone's) is
// running; it comes back when ArgoCD finishes, whatever the result.
export const syncButtonState = (
  status: ArgocdSyncStatus | undefined,
  requesting: boolean,
): { disabled: boolean; label: string } => {
  if (requesting || status?.running) {
    return { disabled: true, label: 'Syncing…' };
  }
  return { disabled: !status, label: 'Sync now' };
};

// Restart: only shown for apps with a Deployment and users who may restart;
// not while a sync runs (it would race the rollout), and not again until the
// pods of the last restart are ready.
export const restartButtonState = (
  status: ArgocdSyncStatus | undefined,
  requesting: boolean,
): { shown: boolean; disabled: boolean; label: string } => {
  const shown = Boolean(status?.canRestart && status.deployments?.length);
  if (requesting || status?.restarting) {
    return { shown, disabled: true, label: 'Restarting…' };
  }
  return { shown, disabled: !status || status.running, label: 'Restart' };
};

const POLL_WHILE_RUNNING_MS = 3000;
const POLL_IDLE_MS = 30000;

const useEntityPath = () => {
  const { entity } = useEntity();
  return `entities/${encodeURIComponent(
    entity.metadata.namespace ?? 'default',
  )}/${encodeURIComponent(entity.kind)}/${encodeURIComponent(
    entity.metadata.name,
  )}`;
};

// One environment's card: Sync now and/or Restart, depending on what the
// signed-in user may do there.
export const ArgocdSyncCard = ({ instance }: { instance: string }) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const alertApi = useApi(alertApiRef);
  const entityPath = useEntityPath();

  const [status, setStatus] = useState<ArgocdSyncStatus>();
  const [requesting, setRequesting] = useState<'sync' | 'restart'>();
  const [confirm, setConfirm] = useState<'sync' | 'restart'>();

  const call = useCallback(
    async (action: 'status' | 'sync' | 'restart') => {
      const baseUrl = await discoveryApi.getBaseUrl('argocd-sync');
      const query = `?instance=${encodeURIComponent(instance)}`;
      const res = await fetchApi.fetch(
        `${baseUrl}/${entityPath}/${action}${query}`,
        {
          method: action === 'status' ? 'GET' : 'POST',
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => undefined);
        throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
      }
      return (await res.json()) as ArgocdSyncStatus;
    },
    [discoveryApi, fetchApi, entityPath, instance],
  );

  const refresh = useCallback(async () => {
    try {
      setStatus(await call('status'));
    } catch {
      // Not configured, not an ArgoCD app, or ArgoCD unreachable: no buttons.
      setStatus(undefined);
    }
  }, [call]);

  // Poll fast while a sync runs or pods are rolling after a restart.
  const busy =
    status?.running ||
    status?.restarting ||
    status?.healthStatus === 'Progressing';

  useEffect(() => {
    refresh();
    const timer = setInterval(
      refresh,
      busy ? POLL_WHILE_RUNNING_MS : POLL_IDLE_MS,
    );
    return () => clearInterval(timer);
  }, [refresh, busy]);

  const envLabel = instance.toUpperCase();

  const run = async (action: 'sync' | 'restart') => {
    setConfirm(undefined);
    setRequesting(action);
    const what = action === 'sync' ? 'Sync' : 'Restart';
    try {
      const next = await call(action);
      setStatus(next);
      alertApi.post({
        message: next.failed?.length
          ? `${what} of ${
              status?.app
            } (${envLabel}) started, but failed for: ${next.failed.join('; ')}`
          : `${what} of ${status?.app} (${envLabel}) started`,
        severity: next.failed?.length ? 'warning' : 'info',
        display: next.failed?.length ? 'permanent' : 'transient',
      });
    } catch (e) {
      alertApi.post({
        message: `${what} of ${status?.app} (${envLabel}) failed to start: ${
          (e as Error).message
        }`,
        severity: 'error',
      });
      refresh();
    } finally {
      setRequesting(undefined);
    }
  };

  if (!status?.canSync && !status?.canRestart) {
    return null;
  }

  const sync = syncButtonState(status, requesting === 'sync');
  const restart = restartButtonState(status, requesting === 'restart');
  const deployments = status.deployments ?? [];

  return (
    <InfoCard
      title={`ArgoCD: ${envLabel}`}
      subheader={`Application: ${status.app} (${instance})`}
    >
      <Box display="flex" alignItems="center" style={{ gap: 16 }}>
        {status.canSync && (
          <Button
            variant="contained"
            color="primary"
            disabled={sync.disabled || !!requesting}
            startIcon={
              sync.disabled ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SyncIcon />
              )
            }
            onClick={() => setConfirm('sync')}
          >
            {sync.label}
          </Button>
        )}
        {restart.shown && (
          <Button
            variant="outlined"
            color="primary"
            disabled={restart.disabled || !!requesting}
            startIcon={
              restart.label === 'Restarting…' ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RestartIcon />
              )
            }
            onClick={() => setConfirm('restart')}
          >
            {restart.label}
          </Button>
        )}
        <Typography variant="body2" color="textSecondary">
          {status.syncStatus ?? 'Unknown'} · {status.healthStatus ?? 'Unknown'}
          {status.operationPhase && !status.running
            ? ` · last sync ${status.operationPhase}${
                status.finishedAt
                  ? ` at ${new Date(status.finishedAt).toLocaleString()}`
                  : ''
              }`
            : ''}
          {status.lastRestartAt
            ? ` · restarted at ${new Date(
                status.lastRestartAt,
              ).toLocaleString()}`
            : ''}
        </Typography>
      </Box>

      <Dialog open={!!confirm} onClose={() => setConfirm(undefined)}>
        <DialogTitle>
          {confirm === 'restart' ? 'Restart' : 'Sync'} {status.app} in{' '}
          {envLabel}?
        </DialogTitle>
        <DialogContent>
          {confirm === 'restart' ? (
            <DialogContentText component="div">
              Rolling restart of {deployments.map(d => d.name).join(', ')}: new
              pods start before the old ones stop, with the same image and
              config. Nothing is deployed from git.
            </DialogContentText>
          ) : (
            <DialogContentText>
              Deploys what is in git for {status.app} to the {envLabel} cluster
              now. Only this app is synced; nothing is pruned.
            </DialogContentText>
          )}
          {instance !== 'dev' && (
            <DialogContentText color="error">
              This is {envLabel}, not dev.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(undefined)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => confirm && run(confirm)}
          >
            {confirm === 'restart' ? 'Restart' : 'Sync'}
          </Button>
        </DialogActions>
      </Dialog>
    </InfoCard>
  );
};

// Cards on the Deployments tab: one per environment where the signed-in user
// may sync or restart this component. Dev: DevOps, the CTO and the owning
// team. QA/UAT: DevOps only. Everyone else sees no card.
export const ArgocdSyncButton = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const entityPath = useEntityPath();
  const [environments, setEnvironments] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('argocd-sync');
        const res = await fetchApi.fetch(
          `${baseUrl}/${entityPath}/environments`,
        );
        const list = res.ok ? ((await res.json()) as ArgocdSyncStatus[]) : [];
        if (!cancelled) {
          setEnvironments(list.map(e => e.instance ?? 'dev'));
        }
      } catch {
        // Not configured or not an ArgoCD app: no cards.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, entityPath]);

  return (
    <>
      {environments.map(instance => (
        <Box key={instance} mb={2}>
          <ArgocdSyncCard instance={instance} />
        </Box>
      ))}
    </>
  );
};
